import * as React from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/components/ui/use-toast"
import { formatAmount } from "@/lib/format"
import { reportWriteError } from "@/lib/writeErrors"
import { cn } from "@/lib/utils"

import { editarCelda, revertirCelda } from "@/routes/finance/api"
import { CLASIFICACIONES } from "@/routes/finance/mapping"
import type { ClasificacionEdicion } from "@/routes/finance/mapping"
import { t } from "@/lib/i18n"

export interface CeldaEditable {
  pais: string
  bloque: string
  account: string
  sub_account: string
  anio: number
  mes: number
  importe_usd: number
  /** Si la celda ya tiene una corrección, su valor original en la hoja. */
  deLaHoja?: number | null
}

/**
 * Editar un importe del modelo financiero.
 *
 * DOS CAMPOS OBLIGATORIOS, y el segundo es el que hace util a este dialogo:
 *
 *  - el MOTIVO, como toda edicion de la aplicacion;
 *  - la NATURALEZA de la diferencia. Finanzas y contabilidad no tienen por que
 *    coincidir: hay cosas que se registran en el libro y no en la gestion, y al
 *    reves. Si todo cambio se guardara como "correccion", cada divergencia
 *    legitima quedaria anotada como si alguien se hubiera equivocado, y la
 *    conciliacion contra el libro reportaria ruido permanente.
 *
 * La hoja NO se toca: la correccion se guarda aparte y gana en la lectura. Se
 * dice en pantalla, porque es lo que permite editar sin miedo a que la proxima
 * importacion se lleve el trabajo por delante.
 */
export function EditarCeldaDialog({
  celda, open, onOpenChange,
}: {
  celda: CeldaEditable | null
  open: boolean
  onOpenChange: (v: boolean) => void
}) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [importe, setImporte] = React.useState("")
  const [motivo, setMotivo] = React.useState("")
  const [clasificacion, setClasificacion] = React.useState<ClasificacionEdicion>("correccion")

  React.useEffect(() => {
    if (celda) {
      setImporte(String(celda.importe_usd))
      setMotivo("")
      setClasificacion("correccion")
    }
  }, [celda])

  function invalidar() {
    for (const k of ["finance-pnl", "finance-cashflow", "finance-kpis",
                     "finance-ratios", "finance-por-pais", "finance-ediciones"]) {
      void queryClient.invalidateQueries({ queryKey: [k] })
    }
  }

  const guardar = useMutation({
    mutationFn: () =>
      editarCelda({
        pais: celda!.pais, bloque: celda!.bloque, account: celda!.account,
        sub_account: celda!.sub_account, anio: celda!.anio, mes: celda!.mes,
        importe_usd: Number(importe), motivo, clasificacion,
      }),
    onSuccess: () => {
      toast({
        variant: "success",
        title: "Importe corregido",
        description: "La hoja no se modificó: reimportarla no pisa este cambio.",
      })
      invalidar()
      onOpenChange(false)
    },
    onError: (e) => reportWriteError(e, { entidad: "el importe", operacion: "editar" }),
  })

  const revertir = useMutation({
    mutationFn: () =>
      revertirCelda({
        pais: celda!.pais, bloque: celda!.bloque, sub_account: celda!.sub_account,
        anio: celda!.anio, mes: celda!.mes,
        motivo: motivo || "Se revierte a lo que dice la hoja",
      }),
    onSuccess: () => {
      toast({ variant: "success", title: "Corrección revertida",
              description: "Vuelve a mandar el valor de la hoja." })
      invalidar()
      onOpenChange(false)
    },
    onError: (e) => reportWriteError(e, { entidad: "la corrección", operacion: "revertir" }),
  })

  if (!celda) return null

  const importeValido = importe.trim() !== "" && Number.isFinite(Number(importe))
  const puedeGuardar = importeValido && motivo.trim().length >= 3
  const tieneCorreccion = celda.deLaHoja != null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("Corregir importe")}</DialogTitle>
          <DialogDescription>
            {celda.sub_account} · {celda.pais} · {celda.anio}-
            {String(celda.mes).padStart(2, "0")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="importe-celda">{t("Importe (USD)")}</Label>
            <Input
              id="importe-celda" type="number" step="0.01" className="num"
              value={importe} onChange={(e) => setImporte(e.target.value)}
            />
            {tieneCorreccion ? (
              <p className="text-2xs text-muted-foreground">
                {t("La hoja dice")}{" "}<span className="num">{formatAmount(celda.deLaHoja ?? 0)}</span>{t(". Esta celda ya está corregida.")}</p>
            ) : null}
          </div>

          <div className="space-y-1">
            <Label id="clasificacion-label">{t("Naturaleza de la diferencia")}</Label>
            <div role="radiogroup" aria-labelledby="clasificacion-label"
                 className="grid gap-1 sm:grid-cols-2">
              {CLASIFICACIONES.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  role="radio"
                  aria-checked={clasificacion === c.id}
                  onClick={() => setClasificacion(c.id)}
                  className={cn(
                    "rounded-md border p-2 text-left transition-colors duration-100",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    clasificacion === c.id
                      ? "border-brand/50 bg-brand-soft"
                      : "hover:border-border-strong",
                  )}
                >
                  <div className="text-xs font-medium">{c.label}</div>
                  <div className="mt-0.5 text-2xs leading-4 text-muted-foreground">
                    {c.ayuda}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="motivo-celda">{t("Motivo")}</Label>
            <Textarea
              id="motivo-celda" rows={2} value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder={t("Por qué se cambia. Queda en el audit log.")}
            />
          </div>

          <p className="rounded-md border border-dashed p-2 text-2xs leading-4 text-muted-foreground">
            {t("La corrección se guarda")}{" "}<strong>aparte</strong> {" "}{t("de la hoja y manda sobre ella. Volver a importar el Google Sheet no la pisa, y se puede revertir en cualquier momento.")}</p>
        </div>

        <DialogFooter className="items-center">
          {tieneCorreccion ? (
            <Button
              variant="outline" size="sm" className="sm:mr-auto"
              onClick={() => revertir.mutate()}
              disabled={revertir.isPending}
            >
              {t("Revertir a la hoja")}</Button>
          ) : null}
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            {t("Cancelar")}</Button>
          <Button
            size="sm"
            disabled={!puedeGuardar || guardar.isPending}
            title={puedeGuardar ? undefined : "Hace falta un importe y un motivo"}
            onClick={() => guardar.mutate()}
          >
            {t("Guardar corrección")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
